type Dimensions = {
  width: number;
  height: number;
};

/**
 * Scales dimensions to fit within a maximum area while maintaining the
 * original aspect ratio. Scales both up and down as needed. Returns the scaled
 * width/height and the scale factor applied (useful for CSS transform: scale on
 * iframe content).
 */
export const scaleDimensions = (original: Dimensions, max: Dimensions) => {
  const scale = Math.min(
    max.width / original.width,
    max.height / original.height,
  );

  return {
    width: Math.round(original.width * scale),
    height: Math.round(original.height * scale),
    scale,
  };
};

/**
 * Removes every `<img …aff_i?… visibility:hidden… />` impression pixel from a
 * creative-code string. Use this BEFORE injecting the code into an iframe /
 * preview render — the pixel adds a hidden zero-size network call that bloats
 * the preview iframe without contributing anything visible. The raw
 * `creative_code` (with pixel) is preserved for the copy-to-clipboard path; we
 * only strip when rendering.
 *
 * Match requires BOTH:
 *  - `aff_i?` (impression-tracker query) somewhere in the `<img>` attributes
 *  - `visibility:hidden` (with optional whitespace, any quote style)
 *
 * Lookaheads `(?=…)` make the two conditions order-independent — works for
 * `style="position:absolute;visibility:hidden;"` and any rearrangement.
 * A `data-dcm-click-tracker="…aff_c?…"` on an `<ins>` tag never matches
 * (regex anchored on `<img>` and requires `aff_i?`).
 */
export const stripImpressionPixel = (code: string): string =>
  code
    .replace(
      /<img(?=[^>]*\baff_i\?)(?=[^>]*visibility:\s*hidden)[^>]*\/?>/gi,
      "",
    )
    .trim();

export const extractImageSrc = (htmlOrUrl: string): string | null => {
  if (!htmlOrUrl) return null;

  const trimmed = htmlOrUrl.trim();
  if (/^(https?:\/\/|data:image\/)/i.test(trimmed)) return trimmed;

  if (typeof DOMParser === "undefined") return null;
  const doc = new DOMParser().parseFromString(trimmed, "text/html");
  return doc.querySelector("img")?.getAttribute("src") ?? null;
};
